import { useMemo, useState } from 'react'
import { Box, Tab, Tabs, Typography } from '@mui/material'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { approvedDisciplineCodesForPractitioner } from 'src/mentor/components/MentorEventsPage/components/eventsListing/formSections/EventTypeConfiguration'
import ConsultationInsights from './insights/ConsultationInsights'
import TherapyInsights from './insights/TherapyInsights'
import ScreeningInsights from './insights/ScreeningInsights'
import PatientInsights from './insights/PatientInsights'
import { ReportTimePeriodSelector, computeRange } from './insights/insightsCharts'

/**
 * Practitioner "My Reports" insights — distinct from the clinic-owner volume stats. One sub-tab per
 * role the practitioner holds (Consultations / Therapy / Screening) plus a cross-cutting Patients
 * tab, scoped to their OWN reports at this clinic location. Each sub-tab fetches lazily on first
 * activation. The panel itself does NOT scroll — it flows inside the page's single scroll; only its
 * sub-tabs + shared time-period selector stay pinned (sticky), anchored just below the fixed page
 * header via {@code stickyTop}.
 */
export default function PractitionerReportInsightsPanel({
  clinicBrandId,
  clinicLocationId,
  mentorProfileData,
  stickyTop = 0,
}) {
  const roles = useMemo(() => getAllRolesInBoolean() || {}, [])
  const approvedDisciplineCodes = useMemo(
    () => approvedDisciplineCodesForPractitioner(mentorProfileData),
    [mentorProfileData],
  )

  const tabs = useMemo(() => {
    const list = []
    if (roles.isDoctor) list.push({ id: 'consultations', label: 'Consultations' })
    if (roles.isTherapist) list.push({ id: 'therapy', label: 'Therapy' })
    if (roles.isScreener) list.push({ id: 'screening', label: 'Screening' })
    // Patient stats apply whenever the practitioner authors any clinical report.
    if (list.length > 0) list.push({ id: 'patients', label: 'Patients' })
    return list
  }, [roles])

  const [activeId, setActiveId] = useState(tabs[0]?.id)
  const [rangeId, setRangeId] = useState('last30')
  const { from, to } = useMemo(() => computeRange(rangeId), [rangeId])

  if (tabs.length === 0) return null

  const currentId = activeId && tabs.some((t) => t.id === activeId) ? activeId : tabs[0]?.id

  return (
    <Box sx={{ backgroundColor: '#fff' }}>
      {/* Sticky bar: role sub-tabs + the shared time-period selector (common across sub-tabs). */}
      <Box
        sx={{
          position: 'sticky',
          top: `${stickyTop}px`,
          zIndex: 4,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: { xs: 1.5, md: 2 },
          pt: 1,
        }}
      >
        <Typography variant="overline" sx={{ color: '#418571', fontWeight: 700, letterSpacing: 1 }}>
          My Reports
        </Typography>
        {tabs.length > 1 ? (
          <Tabs
            value={currentId}
            onChange={(e, v) => setActiveId(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 40, py: 0.5 },
              '& .Mui-selected': { color: '#418571' },
              '& .MuiTabs-indicator': { backgroundColor: '#418571', height: 3 },
            }}
          >
            {tabs.map((t) => (
              <Tab key={t.id} value={t.id} label={t.label} />
            ))}
          </Tabs>
        ) : null}
        <Box sx={{ py: 1.5 }}>
          <ReportTimePeriodSelector value={rangeId} onChange={setRangeId} />
        </Box>
      </Box>

      {/* Content — flows inside the page's single scroll. */}
      <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f5f5f5' }}>
        {tabs.map((t) => {
          const isActive = currentId === t.id
          return (
            <Box key={t.id} sx={{ display: isActive ? 'block' : 'none' }}>
              {t.id === 'consultations' && (
                <ConsultationInsights
                  active={isActive}
                  clinicBrandId={clinicBrandId}
                  clinicLocationId={clinicLocationId}
                  from={from}
                  to={to}
                />
              )}
              {t.id === 'therapy' && (
                <TherapyInsights
                  active={isActive}
                  clinicBrandId={clinicBrandId}
                  clinicLocationId={clinicLocationId}
                  from={from}
                  to={to}
                  approvedDisciplineCodes={approvedDisciplineCodes}
                />
              )}
              {t.id === 'screening' && (
                <ScreeningInsights
                  active={isActive}
                  clinicBrandId={clinicBrandId}
                  clinicLocationId={clinicLocationId}
                  from={from}
                  to={to}
                />
              )}
              {t.id === 'patients' && (
                <PatientInsights
                  active={isActive}
                  clinicBrandId={clinicBrandId}
                  clinicLocationId={clinicLocationId}
                  from={from}
                  to={to}
                />
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
